import { addressToText } from '@dadohr/shared/domainTypes/address';
import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  EnrollmentFormField,
  EnrollmentFormSubmissionStatus,
  FieldType,
  getFieldKey,
  IEnrollmentForm,
  IEnrollmentFormSubmission,
  ProcessSubmissionResponse
} from '@dadohr/shared/domainTypes/enrollmentForms';
import {
  ExperienceAnchorType,
  IExperienceTemplate
} from '@dadohr/shared/domainTypes/experience';
import { DefaultDadoField } from '@dadohr/shared/domainTypes/user';
import { CF } from '@dadohr/shared/versions';
import { Typography } from '@material-ui/core';
import assertNever from 'assert-never';
import { capitalize, compact, startCase } from 'lodash';
import { Check, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import { ButtonWithPromise } from '../../../../../components/ButtonWithPromise';
import { ColoredChipLight } from '../../../../../components/ColoredChip';
import {
  ItemSorter,
  ItemSorters
} from '../../../../../components/GroupableList';
import { IColumn } from '../../../../../components/Table/VirtualizedSortable';
import { useSnackbar } from '../../../../../hooks/useSnackbar';
import { useUnsafeTranslation } from '../../../../../i18next';
import { FlexContainer } from '../../../../../layout/Flex';
import { callFirebaseFunction } from '../../../../../services/firebaseFunctions';
import { toMoment } from '../../../../../services/time';
import { toFullName } from '../../../../../services/user';

type ColumnName =
  | 'submittedBy'
  | 'submittedOn'
  | 'actions'
  | 'enrolledOn'
  | string;

export type D = Doc<IEnrollmentFormSubmission>;
export type O = { enrollmentForm: IEnrollmentForm; orgDateFormat: string };
type EnrollmentFormSubmissionColumn = IColumn<D, ColumnName, O>;

const COLUMNS: EnrollmentFormSubmissionColumn[] = [
  {
    key: 'fullName',
    head: () => 'Full Name',
    cell: (d, o) => getSubmissionFormFullName(d, o.enrollmentForm),
    align: 'left',
    sortable: true,
    defaultDirection: 'desc',
    width: 150,
    flexGrow: 1
  },
  {
    key: 'submittedOn',
    head: () => 'Submitted on',
    cell: (d, o) => toMoment(d.data.createdAt).format(o.orgDateFormat),
    align: 'left',
    sortable: true,
    defaultDirection: 'desc',
    width: 150,
    flexGrow: 1
  },
  {
    key: 'status',
    head: () => 'Status',
    cell: (d) => <StatusChip status={d.data.status} />,
    align: 'left',
    sortable: true,
    defaultDirection: 'desc',
    width: 150,
    flexGrow: 1
  },
  {
    key: 'actions',
    head: () => 'Actions',
    cell: (d) => <Actions submission={d} />,
    align: 'left',
    sortable: true,
    defaultDirection: 'desc',
    width: 200,
    flexGrow: 1
  },
  {
    key: 'enrolledOn',
    head: () => 'Enrolled on',
    cell: (d, o) => {
      if (
        d.data.status === EnrollmentFormSubmissionStatus.COMPLETE &&
        d.data.completedAt
      ) {
        return toMoment(d.data.completedAt).format(o.orgDateFormat);
      }
      return <NotAvailable />;
    },
    align: 'left',
    sortable: true,
    defaultDirection: 'desc',
    width: 150,
    flexGrow: 0
  }
];

export const generateEnrollmentFormFieldColumn = (
  f: EnrollmentFormField
): EnrollmentFormSubmissionColumn => {
  return {
    key: getFieldKey(f),
    head: () => f.renderParameters.displayName,
    matchString: () => f.renderParameters.displayName,
    cell: getEnrollmentFormFieldCell(f),
    align: 'left',
    sortable: true,
    width: 150,
    flexGrow: 1
  };
};

const getEnrollmentFormFieldCell = (
  f: EnrollmentFormField
): ((d: D) => React.ReactNode) => {
  return (d) => {
    const data = d.data.data.find((a) => a.id === f.id);
    if (!data) {
      return <NotAvailable />;
    }
    if (f.type === FieldType.ADDRESS) {
      return addressToText(data.value as any);
    }
    return data.value;
  };
};

const getIdsForFullName = (f: EnrollmentFormField[]) => {
  const firstNameId = f.find((a) =>
    a.targets.find((a) => a.userField === 'firstName')
  )?.id;
  const lastNameId = f.find((a) =>
    a.targets.find((a) => a.userField === 'lastName')
  )?.id;
  const preferredNameId = f.find((a) =>
    a.targets.find((a) => a.userField === 'preferredName')
  )?.id;
  return { firstNameId, lastNameId, preferredNameId };
};

export const getSubmissionFormFullName = (d: D, o: IEnrollmentForm) => {
  const fullNameIds = getIdsForFullName(o.fields);
  const fName = d.data.data
    .find((a) => a.id === fullNameIds.firstNameId)
    ?.value.toString();
  const lName = d.data.data
    .find((a) => a.id === fullNameIds.lastNameId)
    ?.value.toString();
  const pName = d.data.data
    .find((a) => a.id === fullNameIds.preferredNameId)
    ?.value.toString();
  if (fName && lName) {
    return startCase(
      toFullName({ lastName: lName, preferredName: pName || fName })
    );
  }
  return '';
};

type EnrollmentForSubmissionFieldSorter = ItemSorters<D, O>;

const SORTERS: EnrollmentForSubmissionFieldSorter = {
  fullName: {
    key: 'fullName',
    items: {
      sort: (d, o) => getSubmissionFormFullName(d, o.enrollmentForm),
      dir: 'asc'
    }
  },
  submittedOn: {
    key: 'submittedOn',
    items: {
      sort: (d) => (d.data.createdAt ? d.data.createdAt.toMillis() : Infinity),
      dir: 'desc'
    }
  },
  status: {
    key: 'status',
    items: { sort: (d) => d.data.status, dir: 'asc' }
  },
  actions: {
    key: 'actions',
    items: { sort: (d) => d.data.status, dir: 'asc' }
  },
  enrolledOn: {
    key: 'enrolledOn',
    items: {
      sort: (d) =>
        d.data.approvedAt ? d.data.approvedAt.toMillis() : Infinity,
      dir: 'asc'
    }
  }
};

const generateEnrollmentFormFieldSorter = (
  f: EnrollmentFormField
): ItemSorter<D, O> => {
  return {
    key: getFieldKey(f),
    items: {
      sort: (d) => {
        const v = d.data.data.find((a) => a.id === f.id);
        if (!v) {
          return '';
        }
        if (f.type === FieldType.TEXT || f.type === FieldType.EMAIL) {
          return v.value;
        }
        if (f.type === FieldType.DATE) {
          return v.value.valueOf();
        }
        return (v as any) ?? '';
      },
      dir: 'asc'
    }
  };
};

type ProcessSubmissionData = {
  submissionId: string;
  newStatus:
    | EnrollmentFormSubmissionStatus.APPROVED
    | EnrollmentFormSubmissionStatus.REJECTED;
};

const processSubmission = (
  submission: D,
  newStatus:
    | EnrollmentFormSubmissionStatus.APPROVED
    | EnrollmentFormSubmissionStatus.REJECTED
) => {
  const params: ProcessSubmissionData = {
    submissionId: submission.id,
    newStatus
  };
  return callFirebaseFunction<ProcessSubmissionResponse>(
    CF.enrollmentForms.proceessEnrollmentFormSubmission,
    params
  );
};

const Actions = ({ submission }: { submission: D }) => {
  const { t } = useUnsafeTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbarError } = useSnackbar();
  return (
    <FlexContainer>
      <ButtonWithPromise
        size="small"
        variant="outlined"
        pending="Enrolling..."
        noPropagation
        disabled={
          isLoading ||
          submission.data.status === EnrollmentFormSubmissionStatus.COMPLETE ||
          submission.data.status === EnrollmentFormSubmissionStatus.REJECTED
        }
        onClick={async (_) => {
          setIsLoading(true);
          const response = await processSubmission(
            submission,
            EnrollmentFormSubmissionStatus.APPROVED
          );
          if (!response.success && response.errorMessage) {
            enqueueSnackbarError(response.errorMessage);
          }
          setIsLoading(false);
        }}
        startIcon={<Check size={16} />}
      >
        {t('Enroll')}
      </ButtonWithPromise>
      <ButtonWithPromise
        size="small"
        variant="outlined"
        pending="Rejecting..."
        noPropagation
        disabled={
          isLoading ||
          submission.data.status === EnrollmentFormSubmissionStatus.COMPLETE ||
          submission.data.status === EnrollmentFormSubmissionStatus.REJECTED
        }
        onClick={async (_) => {
          setIsLoading(true);
          const response = await processSubmission(
            submission,
            EnrollmentFormSubmissionStatus.REJECTED
          );
          if (!response.success && response.errorMessage) {
            enqueueSnackbarError(response.errorMessage);
          }
          setIsLoading(false);
        }}
        startIcon={<X size={16} />}
      >
        {t('Reject')}
      </ButtonWithPromise>
    </FlexContainer>
  );
};

const StatusChip = ({ status }: { status: EnrollmentFormSubmissionStatus }) => {
  const { t } = useUnsafeTranslation();
  const color = (s: EnrollmentFormSubmissionStatus) => {
    switch (s) {
      case EnrollmentFormSubmissionStatus.APPROVED:
      case EnrollmentFormSubmissionStatus.COMPLETE:
        return 'green';
      case EnrollmentFormSubmissionStatus.PENDING:
        return 'yellow';
      case EnrollmentFormSubmissionStatus.ERROR:
      case EnrollmentFormSubmissionStatus.REJECTED:
      case EnrollmentFormSubmissionStatus.REJECTED_FOR_WRONG_EMAIL_DOMAIN:
        return 'red';
      default:
        return assertNever(s);
    }
  };
  return (
    <ColoredChipLight size="small" color={color(status)}>
      {capitalize(
        t(`{{ status }}`, {
          status:
            status === EnrollmentFormSubmissionStatus.COMPLETE
              ? 'Enrolled'
              : status ===
                EnrollmentFormSubmissionStatus.REJECTED_FOR_WRONG_EMAIL_DOMAIN
              ? 'Invalid'
              : status
        })
      )}
    </ColoredChipLight>
  );
};

const NotAvailable = () => {
  const { t } = useUnsafeTranslation();
  return <Typography color="textSecondary">{t('N/A')}</Typography>;
};

const DEFAULT_SORTER = SORTERS.submittedOn;

export const useEnrollmentFormSubmissionsColumnsAndSorters = (
  enrollmentForm: IEnrollmentForm,
  tpl: Doc<IExperienceTemplate>
): {
  columns: EnrollmentFormSubmissionColumn[];
  sorters: EnrollmentForSubmissionFieldSorter;
  defaultSorter: ItemSorter<D, O>;
} => {
  return useMemo(() => {
    const anchorField =
      tpl.data.anchor.type === ExperienceAnchorType.EMPLOYEE_DATE
        ? tpl.data.anchor.field
        : null;

    const mandatoryFields = compact(
      enrollmentForm.fields.map((f) => {
        const field = f.targets.some(
          (a) =>
            a.userField === DefaultDadoField.PERSONAL_EMAIL ||
            a.userField === DefaultDadoField.EMAIL ||
            a.userField === anchorField
        );
        if (field) {
          return f;
        }
        return null;
      })
    );

    const [fullName, ...rest] = COLUMNS;
    const cols = [
      fullName,
      ...mandatoryFields.map(generateEnrollmentFormFieldColumn),
      ...rest
    ];

    const sorters = { ...SORTERS };

    enrollmentForm.fields.forEach(
      (f) => (sorters[getFieldKey(f)] = generateEnrollmentFormFieldSorter(f))
    );

    return { columns: cols, sorters, defaultSorter: DEFAULT_SORTER };
  }, [enrollmentForm, tpl.data.anchor]);
};
